import { Entity } from '@core/entities/entity'
import { ITemplateForm } from '@core/entities/template/TemplateForm/ITemplateForm'
import {
  ITemplateQuestionGroup,
  TemplateQuestionGroup,
} from '@core/entities/template/TemplateQuestionGroup'
import {
  ITemplateRule,
  TemplateRule,
} from '@core/entities/template/TemplateRule'
import { plainToInstance, Type } from 'class-transformer'

import type { Dictionary } from 'lodash'
import groupBy from 'lodash/groupBy'
import { localizedStrings } from '@core/strings'
import { TemplateWarning } from '@core/entities/template/TemplateWarnings'
import remove from 'lodash/remove'
import { RegisterItem } from '@core/entities/register/RegisterItem'

export class TemplateForm extends Entity<ITemplateForm> {
  @Type(() => TemplateQuestionGroup)
  questions: Array<TemplateQuestionGroup>

  @Type(() => TemplateRule)
  rules?: Array<TemplateRule>

  @Type(() => TemplateWarning)
  warningsConfig?: Array<TemplateWarning>

  public deleteQuestion({
    groupIndex,
    questionIndex,
  }: {
    groupIndex: number
    questionIndex?: number
  }) {
    const templateFormClone: ITemplateForm = JSON.parse(
      JSON.stringify(this.getInitialValues()),
    )
    const deleteQuestionWarnings = (questionId: string) => {
      remove(
        templateFormClone.warningsConfig,
        (warning) => warning.questionId === questionId,
      )
    }
    if (questionIndex !== undefined) {
      deleteQuestionWarnings(
        templateFormClone.questions[groupIndex]?.data[questionIndex]?.id,
      )
      templateFormClone.questions[groupIndex]?.data?.splice(questionIndex, 1)
    } else {
      templateFormClone.questions[groupIndex]?.data?.forEach((question) => {
        deleteQuestionWarnings(question.id)
      })
      templateFormClone.questions.splice(groupIndex, 1)
    }
    return templateFormClone
  }

  public isAnswerUsedByRule(
    answerId: string,
    rules: Array<ITemplateRule>,
  ): boolean {
    return rules?.some((rule) =>
      rule.conditions.some((condition) =>
        condition.terms?.find((term) => term.answerId === answerId),
      ),
    )
  }

  public hasRulePointingToQuestion(
    id: string,
    rules?: Array<ITemplateRule>,
  ): boolean {
    const list = rules || this.rules
    return list?.some((rule) =>
      rule.shownQuestions.find((thenShow) => thenShow.questionId === id),
    )
  }

  public hasQuestionPointingToRules(
    id: string,
    rules: Array<ITemplateRule>,
  ): boolean {
    let result = false
    rules.forEach((rule) => {
      if (rule.conditions.find((condition) => condition.questionId === id)) {
        result = true
      }
    })
    return result
  }

  public getVisibilityTexts(id: string): string[] {
    const questionIds: string[] = []
    const texts: string[] = []
    const answerIds: Array<string | number> = []

    this.rules?.forEach((rule) => {
      if (rule.shownQuestions.find((thenShow) => thenShow.questionId === id)) {
        rule.conditions.forEach((condition) => {
          // PROJECT_IS_IN conditions use projects instead of question answers
          if (condition.type === 'PROJECT_IS_IN') {
            const projectNames = condition.projects?.map((p) => p.name) ?? []

            if (projectNames.length === 1) {
              texts.push(localizedStrings.visibleForProject(projectNames[0]))
            } else if (projectNames.length > 1) {
              texts.push(
                localizedStrings.visibleForNProjects(projectNames.length),
              )
            }
            return
          }

          questionIds.push(condition.questionId)
          condition.terms?.forEach((term) => {
            answerIds.push(term.answerId)
          })
        })
      }
    })

    this.questions.forEach((qGroup, gIndex) => {
      qGroup.data.forEach((question, qIndex) => {
        if (questionIds.includes(question.id)) {
          const answers = []
          question.answers?.forEach((answer) => {
            if (answerIds.includes(answer.id)) {
              answers.push(answer.value)
            }
          })
          texts.push(
            localizedStrings.visibleWhenQuestionIs(
              `${gIndex + 1}-${qIndex + 1}`,
              answers.join(', '),
            ),
          )
        }
      })
    })

    return texts
  }

  public getMultipleChoiceQuestionAnswers = (questionId: string) => {
    for (const questionGroup of this.questions) {
      const question = questionGroup.data.find(
        (q) => q.id === questionId && q?.questionType === 'MULTIPLE_CHOICE',
      )
      if (question) {
        return question.answers || []
      }
    }
    return []
  }

  /** Indexes rules by every condition's questionId so multi-condition rules appear under all controlling questions. */
  public getRulesByDependentQuestionId(): Dictionary<ITemplateRule[]> {
    const result: Dictionary<ITemplateRule[]> = {}
    this.rules?.forEach((rule) => {
      rule.conditions?.forEach((condition) => {
        if (condition.questionId) {
          if (!result[condition.questionId]) {
            result[condition.questionId] = []
          }
          result[condition.questionId].push(rule)
        }
      })
    })
    return result
  }

  public getRulesByTargetQuestionId(): Dictionary<ITemplateRule[]> {
    return groupBy(
      this.rules,
      ({ shownQuestions: [thenShow] }) => thenShow.questionId,
    )
  }

  public hasMultipleChoiceQuestion(): boolean {
    return this.questions.some((group) =>
      group.data.some(
        (question) => question.questionType === 'MULTIPLE_CHOICE',
      ),
    )
  }

  public getMultipleChoiceQuestions() {
    return this?.questions
      .map((questionGroup, key) => {
        return questionGroup.data
          .map((question, i) => {
            return {
              ...question,
              groupIndex: key,
              questionIndex: i,
            }
          })
          .filter((question) => question.questionType === 'MULTIPLE_CHOICE')
      })
      .flat()
  }

  public static getUnansweredInspectionsCount(
    questions: ITemplateQuestionGroup[],
    hiddenQuestionIds?: Array<string>,
  ) {
    const hiddenSet = new Set(hiddenQuestionIds)
    let count = 0
    questions?.forEach((questionGroup: ITemplateQuestionGroup) => {
      questionGroup.data.forEach((question) => {
        if (
          question.questionType === 'INSPECTION_ITEM' &&
          !question.inspectionItemAnswers?.length &&
          !hiddenSet.has(question.id)
        ) {
          count += 1
        }
      })
    })
    return count
  }

  public getInitialValues({
    registerItem,
  }: { registerItem?: RegisterItem } = {}): ITemplateForm {
    const wrapper = this.toPlain()
    return {
      questions: wrapper.questions.map((questionGroup) => ({
        ...questionGroup,
        data: questionGroup.data.map((question) => {
          if (question.questionType === 'LINK_REGISTER_ITEMS') {
            return {
              ...question,
              linkedRegisterTemplateId: question.linkedRegisterTemplate?.id,
              previewPropertyIds:
                question.previewProperties?.map(({ id }) => id) || [],
              pinnedFilterIds:
                question.pinnedFilters?.map(({ id }) => id) || [],
              registerItems:
                registerItem && question.preSelected
                  ? [registerItem?.toPlain()]
                  : question.registerItems?.map(({ toPlain }) => toPlain()) ||
                    [],
              registerItemIds:
                registerItem && question.preSelected
                  ? [registerItem?.id]
                  : question.registerItems?.map(({ id }) => id) || [],
            }
          } else {
            return question
          }
        }),
      })),
      rules: wrapper.rules || [],
      warningsConfig: wrapper.warningsConfig || [],
    }
  }

  public static new(payload: unknown): TemplateForm {
    const entity = plainToInstance(TemplateForm, payload)

    return entity
  }
}
